/** @jsx jsx */

import { jsx, Spinner, Text } from "theme-ui";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFirestore } from "react-redux-firebase";
import {
  doFormatShortDate,
  formatSys2OdooDate,
} from "@heartfulnessinstitute/react-hfn-forms/dist/formatting";
import PropTypes from "prop-types";
import { groupBy } from "sites-common/utils/lodash";
import { ActionButton } from "office-ui-fabric-react";
import P0Header from "./P0Header";

import { useLang } from "./LanguageSelector";
import NewStayRecord from "./NewStayRecord";
import FormContext from "./FormContext";
import RenderPersonVisit from "./RenderPersonVisit";
import IndividualFormC from "./IndividualFormC";
import RenderPersonVisitsAdmin from "./RenderPersonVisitsAdmin";
import { PIVOT_CHECKEDIN } from "./constants";

const { shade6 } = require("./json/colors.json");
const { collection } = require("./json/firebase.json");

const listenerSettings1 = (listenerParams) => ({
  ...listenerParams,
  collection,
  storeAs: collection,
});

// const today = formatSys2OdooDate(new Date());

function MyStayRecords({
  admin,
  queryParams,
  initialLimit,
  uid,
  defaults,
  pivot,
  mistakes,
}) {
  const [p2, setP2] = useState(null);

  const [limit, setLimit] = useState(initialLimit);

  const records = useSelector((state) =>
    collection in state.firestore.ordered
      ? state.firestore.ordered[collection]
      : []
  );

  const count = records.length;
  const firestore = useFirestore();

  const [getLangString] = useLang();

  const onAddNew = useCallback(
    (payload, successCb, failureCb) => {
      const { arrival, departure, passport_num, full_name } = payload;

      const dupCheck = records.some(
        (r) =>
          r.arrival === arrival &&
          r.departure === departure &&
          ((!!r.passport_num &&
            !!passport_num &&
            r.passport_num === passport_num) ||
            r.full_name === full_name)
      );
      if (dupCheck) {
        failureCb(getLangString("errors.dup_visitor"));
        return;
      }

      const formcTransforms = (payload1) => ({
        applicant_doarrivalhotel: payload1.arrival,
        date_departure: payload1.departure,
        applicant_passpno: payload1.passport_num || "",
        ...payload1.formc,
      });

      firestore
        .add(collection, {
          ...payload,
          formc: formcTransforms(payload),
          uid,
          create_date: formatSys2OdooDate(new Date()),
          vers: 0,
          stay_status: "",
        })
        .then((docRef) => {
          successCb(docRef.id);
        })
        .catch((e) => failureCb(e.toString()));
    },
    [uid, firestore, getLangString, records]
  );

  const onSaveFormC = useCallback(
    (oldPayload, newFormC, success_callback, failure_callback) => {
      const partialPayload = {
        arrival: newFormC.applicant_doarrivalhotel,
        departure: newFormC.date_departure,
        passport_num: newFormC.applicant_passpno,
        formc: {
          ...oldPayload.formc,
          ...newFormC,
        },
      };
      const docId = oldPayload.id;

      return firestore
        .set(`${collection}/${docId}`, partialPayload, { merge: true })
        .then(success_callback)
        .catch((e) => failure_callback(e.toString()));
    },
    [firestore]
  );

  const onSaveRecord = useCallback(
    (payload, success_callback, failure_callback) => {
      const docId = payload.id;

      return firestore
        .set(`${collection}/${docId}`, payload, { merge: true })
        .then(success_callback)
        .catch((e) => failure_callback(e.toString()));
    },
    [firestore]
  );

  useEffect(() => {
    const listenerParams = admin
      ? { ...queryParams, limit }
      : { where: ["uid", "==", uid], ...(limit ? { limit } : {}) };
    const listenerSettings = { ...listenerSettings1(listenerParams) };
    firestore.setListener(listenerSettings);
    return function cleanup() {
      firestore.unsetListener(listenerSettings);
    };
  }, [firestore, admin, queryParams, limit, uid]);

  const groupedRecs = groupBy(records, (r) => `${r.arrival}~${r.departure}`);

  const discardFormC = useCallback(() => setP2(null), [setP2]);

  const loadMore = useCallback(() => {
    setLimit(
      (x) => x + (!initialLimit || initialLimit > 20 ? 20 : initialLimit)
    );
  }, [initialLimit]);

  return (
    <FormContext.Provider
      value={{
        setP2,
        uid,
        onAddNew,
        p2,
        discardFormC,
        onSaveFormC,
        admin,
        onSaveRecord,
        pivot,
      }}
    >
      {!!p2 && <IndividualFormC key={p2.record.id} defaults={defaults} />}
      {!p2 && (
        <div>
          {!admin && <P0Header />}
          <div sx={{ m: 2 }}>
            {records === undefined && <Spinner />}
            {records !== undefined && (
              <div>
                {((admin && pivot === PIVOT_CHECKEDIN) || !admin) && (
                  <NewStayRecord />
                )}

                {/* {records.length === 0 && (
              <div sx={{ textAlign: "center", m: 2 }}>
                <Text variant="description"> No Entries Available </Text>
              </div>
            )} */}

                {admin && (
                  <div>
                    <RenderPersonVisitsAdmin
                      records={records}
                      mistakes={mistakes}
                    />
                  </div>
                )}

                {!admin &&
                  Object.entries(groupedRecs)
                    .sort((a, b) => a[0] < b[0])
                    .map(([, recs]) => (
                      <div sx={{ my: 3, p: 2, bg: shade6 }}>
                        <Text variant="header">
                          {doFormatShortDate(recs[0].arrival)} -{" "}
                          {doFormatShortDate(recs[0].departure)}{" "}
                        </Text>
                        {recs.map((r) => (
                          <RenderPersonVisit key={r.id} record={r} />
                        ))}

                        {/* <NewStayRecord
                btnNewVisitText="buttons.add_new_person"
                fixedValues={{  arrival: k.split("~")[0], departure: k.split("~")[1] }}
                  /> */}
                      </div>
                    ))}

                {!!limit && limit === count && (
                  <div>
                    <ActionButton
                      iconProps={{ iconName: "Down" }}
                      text="load more"
                      onClick={loadMore}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </FormContext.Provider>
  );
}

MyStayRecords.propTypes = {
  admin: PropTypes.bool,
  uid: PropTypes.string,
  defaults: PropTypes.shape({}),
  queryParams: PropTypes.shape([]),
  pivot: PropTypes.string,
  initialLimit: PropTypes.number,
  mistakes: PropTypes.bool,
};
MyStayRecords.defaultProps = {
  admin: false,
  uid: 0,
  queryParams: { limit: 10 },
  defaults: {},
  pivot: undefined,
  initialLimit: undefined,
  mistakes: false,
};

export default MyStayRecords;
